import { faro } from '@grafana/faro-react';
import { Attributes, Exception, SpanStatusCode } from '@opentelemetry/api';

/**
 * Higher order function to wrap existing functions to add tracing
 * @param name Name of the span - suggest using namespacing to uniquely identify the span
 * @param func The function to wrap
 * @param spanAttributesFromArgs Optionally create attributes for the span based on the function arguments
 * This can also be done manually by setting attributes on the current active span
 * @returns Wrapped version of the function, with tracing added
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- required to handle any function
export function span<This, Args extends any[], Return>(
  name: string,
  func: (this: This, ...args: Args) => Return,
  spanAttributesFromArgs?: (...args: Args) => Attributes
) {
  return function (this: This, ...args: Args): Return {
    const otel = faro.api.getOTEL();
    if (!otel) {
      return func.call(this, ...args);
    }
    const tracer = otel.trace.getTracer('span');
    const span = tracer.startSpan(name, { attributes: spanAttributesFromArgs?.(...args) });
    return otel.context.with(otel.trace.setSpan(otel.context.active(), span), () => {
      try {
        const result = func.call(this, ...args);
        return result;
      } catch (ex) {
        span.recordException(ex as Exception);
        span.setStatus({ code: SpanStatusCode.ERROR });
        throw ex;
      } finally {
        span.end();
      }
    });
  };
}

/**
 * Higher order function to wrap existing async functions to add a trace
 * @example
 * ```ts
 * function myAsyncFunc(): Promise<Return> {
 *   return doSomething().then(result => processResult(result))
 * }
 * export const wrappedAsyncFunc = asyncSpan('someFeature.myAsyncFunc', myAsyncFunc)
 * ```
 * NB: This doesn't work if the wrapped function uses async/await!
 * See https://github.com/open-telemetry/opentelemetry-js/issues/3773
 * and https://github.com/angular/angular/issues/31730
 * Workaround is to use old-school promise chains
 * @param name Name of the span
 * @param func The async function to wrap
 * @param spanAttributesFromArgs Optionally create attributes for the span based on the function arguments
 * This can also be done manually by setting attributes on the current active span
 * @returns A wrapped version of the async function, with tracing added
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- required to handle any function
export function asyncSpan<This, Args extends any[], Return>(
  name: string,
  func: (this: This, ...args: Args) => Promise<Return>,
  spanAttributesFromArgs?: (...args: Args) => Attributes
) {
  return function (this: This, ...args: Args): Promise<Return> {
    const otel = faro.api?.getOTEL();
    if (!otel) {
      return func.call(this, ...args);
    }
    const tracer = otel.trace.getTracer('span');
    const span = tracer.startSpan(name, { attributes: spanAttributesFromArgs?.(...args) });
    return otel.context.with(otel.trace.setSpan(otel.context.active(), span), async () => {
      try {
        const result = await func.call(this, ...args);
        return result;
      } catch (ex) {
        span.recordException(ex as Exception);
        span.setStatus({ code: SpanStatusCode.ERROR });
        throw ex;
      } finally {
        span.end();
      }
    });
  };
}
